import { EditorStarterItem } from '../../items/item-type';
import { resolveGroupGeometry } from '../../items/group/group-geometry';
import { getRectAfterCrop } from '../../utils/get-dimensions-after-crop';

export type AlignAxis =
  | 'left'
  | 'h-center'
  | 'right'
  | 'top'
  | 'v-center'
  | 'bottom';

export interface AlignRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * 정렬 기준 rect. 그룹은 bbox 를 SoT 로 저장하지 않으므로(stale 가능) children union 으로
 * 계산한다. 이동 자체는 left/top delta 를 translateGroupChildren 이 children 에 전파한다.
 */
export const getAlignRect = (
  item: EditorStarterItem,
  items: Record<string, EditorStarterItem>,
): AlignRect =>
  item.type === 'group'
    ? resolveGroupGeometry(item, items)
    : getRectAfterCrop(item);

/** 선택된 rect 들의 합집합 bbox. 빈 배열이면 null (정렬 no-op 처리용). */
export const computeUnionBounds = (rects: AlignRect[]): AlignRect | null => {
  if (rects.length === 0) return null;
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const r of rects) {
    left = Math.min(left, r.left);
    top = Math.min(top, r.top);
    right = Math.max(right, r.left + r.width);
    bottom = Math.max(bottom, r.top + r.height);
  }
  return { left, top, width: right - left, height: bottom - top };
};

/** bounds 안에서 rect 를 axis 로 정렬했을 때의 이동량. */
export const computeAlignOffset = (
  axis: AlignAxis,
  bounds: AlignRect,
  rect: AlignRect,
): { dx: number; dy: number } => {
  switch (axis) {
    case 'left':
      return { dx: bounds.left - rect.left, dy: 0 };
    case 'h-center':
      return {
        dx:
          Math.round(bounds.left + (bounds.width - rect.width) / 2) - rect.left,
        dy: 0,
      };
    case 'right':
      return { dx: bounds.left + bounds.width - rect.width - rect.left, dy: 0 };
    case 'top':
      return { dx: 0, dy: bounds.top - rect.top };
    case 'v-center':
      return {
        dx: 0,
        dy:
          Math.round(bounds.top + (bounds.height - rect.height) / 2) - rect.top,
      };
    case 'bottom':
      return { dx: 0, dy: bounds.top + bounds.height - rect.height - rect.top };
  }
};
